/**
 * MissedUpgradesPanel — Marketing tab tool. Finds buyers whose add-on count
 * is below their ticket count and emails them a top-up link (from
 * team@eventism.com, "We noticed…").
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Gift, Loader2, Mail, Send, TrendingUp, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token') || ''}` } });

export default function MissedUpgradesPanel({ eventId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [sending, setSending] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/events/${eventId}/missed-upgrades`, auth());
      setData(res.data);
      const init = {};
      (res.data.orders || []).forEach(o => { if (!o.emailed_at) init[o.order_id] = true; });
      setSelected(init);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load missed upgrades');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [eventId]);

  const sendEmails = async () => {
    const ids = Object.entries(selected).filter(([_, v]) => v).map(([k]) => k);
    if (ids.length === 0) { toast.error('Select at least one buyer'); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/events/${eventId}/missed-upgrades/send`,
        { order_ids: ids, resend: true }, auth());
      toast.success(`Sent ${res.data.sent} upgrade email${res.data.sent !== 1 ? 's' : ''}${res.data.failed ? ` · ${res.data.failed} failed` : ''}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-indigo-400" /></div>;
  if (!data) return null;

  const orders = data.orders || [];
  const selectedCount = Object.values(selected).filter(Boolean).length;
  const allSelected = orders.length > 0 && orders.every(o => selected[o.order_id]);

  return (
    <div className="space-y-5" data-testid="missed-upgrades-panel">
      <div>
        <h2 className="text-lg font-semibold mb-1 flex items-center gap-2"><Gift className="h-4 w-4 text-fuchsia-400" /> Missed Upgrades</h2>
        <p className="text-sm text-muted-foreground">
          Buyers with fewer upgrades than tickets get a "We noticed…" email from team@eventism.com with a one-click link to top up their original order.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card className="border-border"><CardContent className="p-4 text-center">
          <TrendingUp className="h-4 w-4 text-amber-400 mx-auto mb-1" />
          <p className="text-xl font-bold" data-testid="mu-stat-pct">{data.stats.upgrade_pct}%</p>
          <p className="text-[11px] text-muted-foreground">buyers upgraded</p>
        </CardContent></Card>
        <Card className="border-border"><CardContent className="p-4 text-center">
          <Users className="h-4 w-4 text-indigo-400 mx-auto mb-1" />
          <p className="text-xl font-bold" data-testid="mu-stat-missing">{orders.length}</p>
          <p className="text-[11px] text-muted-foreground">missing upgrades</p>
        </CardContent></Card>
        <Card className="border-border"><CardContent className="p-4 text-center">
          <Gift className="h-4 w-4 text-fuchsia-400 mx-auto mb-1" />
          <p className="text-xl font-bold" data-testid="mu-stat-addons">{(data.addons || []).length}</p>
          <p className="text-[11px] text-muted-foreground">upgrades on sale</p>
        </CardContent></Card>
      </div>

      {(data.addons || []).length === 0 ? (
        <Card className="border-border"><CardContent className="p-6 text-center text-sm text-muted-foreground">
          No in-stock add-ons on this event — create an add-on in the Tickets tab first.
        </CardContent></Card>
      ) : orders.length === 0 ? (
        <Card className="border-emerald-500/30"><CardContent className="p-6 text-center text-sm text-muted-foreground" data-testid="mu-empty">
          Every buyer's upgrades match their ticket count. Nothing to chase. 🎉
        </CardContent></Card>
      ) : (
        <Card className="border-border">
          <CardContent className="p-0">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
                <Checkbox checked={allSelected} onCheckedChange={(v) => {
                  const next = {};
                  orders.forEach(o => { next[o.order_id] = !!v; });
                  setSelected(next);
                }} data-testid="mu-select-all" /> Select all
              </label>
              <Button size="sm" className="rounded-full font-semibold" disabled={sending || selectedCount === 0} onClick={sendEmails} data-testid="mu-send-btn">
                {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
                Send upgrade email ({selectedCount})
              </Button>
            </div>
            {orders.map(o => (
              <div key={o.order_id} className="flex items-center gap-3 px-4 py-2.5 border-b border-border last:border-0" data-testid={`mu-row-${o.order_id}`}>
                <Checkbox checked={!!selected[o.order_id]} onCheckedChange={(v) => setSelected(prev => ({ ...prev, [o.order_id]: !!v }))} data-testid={`mu-check-${o.order_id}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{o.buyer_name || o.buyer_email}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{o.buyer_email}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs">{o.tickets} ticket{o.tickets !== 1 ? 's' : ''} · {o.upgrades} upgrade{o.upgrades !== 1 ? 's' : ''}</p>
                  <p className="text-[11px] font-semibold text-fuchsia-400">{o.missing} missing</p>
                </div>
                {o.emailed_at && <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[9px] gap-1 flex-shrink-0" data-testid={`mu-emailed-${o.order_id}`}><Mail className="h-2.5 w-2.5" /> Emailed</Badge>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <p className="text-[11px] text-muted-foreground">
        Email opens with "We noticed you grabbed X tickets…", shows that {data.stats.upgrade_pct}% of buyers upgraded, lists the available upgrades, and links to a page where they add them to their existing order — no re-buying tickets.
      </p>
    </div>
  );
}
